import { RolexContainer } from '../Container'
import { getYoutubeVideoId } from './helpers/get-youtube-video-id'
import { Icon, Image } from '@components/gassan-ui'
import { cn } from '@lib/cn'
import { getKontentImage } from '@lib/kontent'
import { AssetsElement } from '@lib/kontent/models/Elements'
import { FC, useState } from 'react'

type RolexVideoProps = {
  className?: string
  placeholderImage: AssetsElement
  youtubeVideoId: string
  size?: string
  spacing?: 'none' | 'small' | 'large'
  spacingType?: 'padding' | 'margin'
}

export const RolexVideo: FC<RolexVideoProps> = ({
  placeholderImage,
  youtubeVideoId,
  className,
  spacing = 'none',
  spacingType = 'padding',
  size = 'm',
}) => {
  const hasPlaceholderImage = placeholderImage && placeholderImage.value.length > 0
  const [status, setStatus] = useState<'idle' | 'playing'>(hasPlaceholderImage ? 'idle' : 'playing')
  const id = getYoutubeVideoId(youtubeVideoId)

  return (
    <RolexContainer
      className={cn(
        `grid w-full grid-cols-6 gap-2 md:grid-cols-12`,
        spacingType === 'padding' &&
          (spacing === 'small' ? 'pb-[40px]' : spacing === 'large' ? 'pb-[60px] lg:pb-[90px]' : ''),
        spacingType === 'margin' &&
          (spacing === 'small' ? 'mb-[40px]' : spacing === 'large' ? 'mb-[60px] lg:mb-[90px]' : ''),
        className,
      )}
    >
      <div
        className={cn(
          'relative col-span-6 aspect-video md:col-span-10 md:col-start-2',
          size === 'xl' && 'md:col-span-12 md:col-start-1',
        )}
      >
        {placeholderImage && (
          <>
            <Image
              className={cn('h-full w-full object-cover', status === 'playing' && 'hidden')}
              sources={[768, 768, 1200, 1600].map((width) =>
                getKontentImage(placeholderImage.value[0]?.url, {
                  width,
                }),
              )}
              placeholderSrc={getKontentImage(placeholderImage.value[0]?.url, {
                width: 50,
                height: 50,
              })}
              alt={placeholderImage.value[0]?.description as string}
            />
            <div
              className={cn(
                'absolute left-1/2 top-1/2 z-[3] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center opacity-100',
                status === 'playing' && 'hidden',
              )}
            >
              <button
                className={cn(
                  'relative flex h-[60px] w-[60px] items-center justify-center rounded-full bg-white text-rolexBrown hover:text-rolexPrimary',
                )}
                onClick={requestVideoPlay}
                tabIndex={-1}
              >
                <Icon icon="play" size={26} />
              </button>
            </div>
          </>
        )}

        {status === 'playing' && (
          <div className="pointer-events-auto absolute inset-0 h-full w-full">
            <iframe
              className="h-full w-full"
              src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=${
                hasPlaceholderImage ? 1 : 0
              }`}
              title="Rolex video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}
      </div>
    </RolexContainer>
  )

  function requestVideoPlay() {
    setStatus('playing')
  }
}
